import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { isInterceptorReported403 } from '@/modules/featurerestriction/errorCodes'
import { extractApiErrorMessage, getApiErrorStatus, isNetworkError } from '@/util/api-error'
import i18n from '@/util/config/i18n'

export type ApiErrorSnackbarOptions = {
  dedupeKey?: string
  onRetry?: () => void
  retryLabel?: string
  /** Show `fallbackLabel` even when the server sent a message (it may be untranslated, or not say what failed) */
  preferFallbackLabel?: boolean
}

const NETWORK_ERROR_DEDUPE_KEY = 'api-network-error'
const SAVE_FAILED_DEDUPE_KEY = 'save-failed'
const UNSAVED_RETRY_DEDUPE_KEY = 'save-failed-retry'
const reportedNetworkErrors = new WeakSet<object>()
const queuedNetworkErrors = new WeakMap<object, number>()

/**
 * Shows one connection error for a failed request. A call site can supply a retry action that
 * re-runs its own operation instead of reloading the whole application.
 */
export function enqueueNetworkErrorSnackbar(
  error: unknown,
  options: Pick<ApiErrorSnackbarOptions, 'onRetry' | 'retryLabel'> = {}
): string | undefined {
  if (error === null || typeof error !== 'object' || !isNetworkError(error)) {
    return undefined
  }

  const queuedTimer = queuedNetworkErrors.get(error)
  if (queuedTimer !== undefined) {
    window.clearTimeout(queuedTimer)
    queuedNetworkErrors.delete(error)
  }

  if (reportedNetworkErrors.has(error)) {
    return undefined
  }

  const { onRetry, retryLabel } = options
  reportedNetworkErrors.add(error)
  return useSnackbarStore().enqueue({
    label: i18n.t('general.networkError') as string,
    variant: 'alert',
    leadingIcon: true,
    dedupeKey: NETWORK_ERROR_DEDUPE_KEY,
    autoHide: !onRetry,
    buttonLabel: onRetry ? retryLabel ?? (i18n.t('general.tryAgain') as string) : undefined,
    onButtonClick: onRetry
  })
}

/**
 * Defers the generic snackbar long enough for a caller's catch handler to provide a contextual
 * retry action. Calls without an error handler still receive the network message.
 */
export function queueNetworkErrorSnackbar(error: unknown): void {
  if (error === null || typeof error !== 'object' || !isNetworkError(error)) {
    return
  }

  if (reportedNetworkErrors.has(error) || queuedNetworkErrors.has(error)) {
    return
  }

  const timer = window.setTimeout(() => {
    queuedNetworkErrors.delete(error)
    enqueueNetworkErrorSnackbar(error)
  }, 0)
  queuedNetworkErrors.set(error, timer)
}

/**
 * Marks a network error as handled without showing anything. For callers that recover on their own,
 * such as the save coordinator retrying quietly; it reports a final failure itself. Must run in the
 * same tick as the rejection, before the queued generic snackbar fires.
 */
export function claimNetworkError(error: unknown): void {
  if (error === null || typeof error !== 'object') {
    return
  }
  const queuedTimer = queuedNetworkErrors.get(error)
  if (queuedTimer !== undefined) {
    window.clearTimeout(queuedTimer)
    queuedNetworkErrors.delete(error)
  }
  reportedNetworkErrors.add(error)
}

/**
 * Shows the backend's own error description (falling back to `fallbackLabel`) as a warning snackbar,
 * optionally with a retry action.
 *
 * Returns undefined without enqueueing anything only where `ApiClient`'s response interceptor has
 * already told the user: 401 (token refresh, then logout) and the two coded 403s it turns into an
 * upgrade nudge. Toasting here as well would double up on those paths.
 *
 * Every other 403 — access revoked, recipient removed from the space, an endpoint the role cannot
 * reach — is reported normally. The interceptor only recognises `trial-soft-locked` and
 * `plan-entitlement-required`, so suppressing 403 wholesale left ordinary authorization failures
 * with no message at all, which reads as "this really is empty" rather than "this failed".
 */
export function enqueueApiErrorSnackbar(
  error: unknown,
  fallbackLabel: string,
  options: ApiErrorSnackbarOptions = {}
): string | undefined {
  const networkErrorSnackbarId = enqueueNetworkErrorSnackbar(error, options)
  if (networkErrorSnackbarId !== undefined) {
    return networkErrorSnackbarId
  }

  if (isNetworkError(error)) {
    return undefined
  }

  if (getApiErrorStatus(error) === 401 || isInterceptorReported403(error)) {
    return undefined
  }

  const { dedupeKey, onRetry, retryLabel, preferFallbackLabel } = options

  return useSnackbarStore().enqueue({
    label: preferFallbackLabel ? fallbackLabel : extractApiErrorMessage(error) ?? fallbackLabel,
    variant: 'warning',
    leadingIcon: true,
    dedupeKey,
    // A retry affordance is useless if it auto-hides before the user reaches it.
    autoHide: !onRetry,
    buttonLabel: onRetry ? retryLabel ?? (i18n.t('general.tryAgain') as string) : undefined,
    onButtonClick: onRetry
  })
}

/**
 * Tells the user the server rejected a save, so the edit is not lost silently. One snackbar however many editors fail
 * at once (dedupe key). Only answered requests count: a network failure is already reported by ApiClient, and a
 * cancelled request (logout, navigation) or an error thrown before a request is not something to alarm the user with.
 */
export function enqueueSaveFailedSnackbar(error: unknown): void {
  if (getApiErrorStatus(error) === undefined) return
  // Always the localized "not saved" text: a server message may be English only and does not say the edit was lost
  enqueueApiErrorSnackbar(error, i18n.t('textEditor.saveFailed') as string, {
    dedupeKey: SAVE_FAILED_DEDUPE_KEY,
    preferFallbackLabel: true
  })
}

/**
 * Tells the user a change did not reach the server after the save coordinator gave up on it (retries used up, or a
 * create, delete or move failed). Unlike enqueueSaveFailedSnackbar it also speaks for a network failure: the
 * coordinator claimed that error to retry quietly, so ApiClient's connection message never showed. Same dedupe key,
 * so editors and the coordinator together still show one snackbar.
 */
export function enqueueUnsavedChangeSnackbar(error: unknown): void {
  if (getApiErrorStatus(error) !== undefined) {
    enqueueSaveFailedSnackbar(error)
    return
  }
  useSnackbarStore().enqueue({
    label: i18n.t('textEditor.saveFailed') as string,
    variant: 'warning',
    leadingIcon: true,
    dedupeKey: SAVE_FAILED_DEDUPE_KEY
  })
}

/**
 * Tells the user changes did not reach the server but are kept in this tab (the save coordinator parked them), with
 * a Retry. One snackbar however many writes park; it stays until Retry, a dismissal, or
 * `dismissUnsavedChangeRetrySnackbar` once nothing is parked any more. It carries no space id, so leaving the space
 * keeps it: the changes are still in this tab.
 */
export function enqueueUnsavedChangeRetrySnackbar(error: unknown, onRetry: () => void): void {
  useSnackbarStore().enqueue({
    // The server's own reason when it rejected the write (e.g. a validation message)
    label: extractApiErrorMessage(error) ?? (i18n.t('saveStatus.failedSnackbar') as string),
    variant: 'warning',
    leadingIcon: true,
    dedupeKey: UNSAVED_RETRY_DEDUPE_KEY,
    autoHide: false,
    buttonLabel: i18n.t('saveStatus.retry') as string,
    onButtonClick: onRetry
  })
}

export function dismissUnsavedChangeRetrySnackbar(): void {
  const snackbarStore = useSnackbarStore()
  snackbarStore.items
    .filter(item => item.dedupeKey === UNSAVED_RETRY_DEDUPE_KEY && item.id)
    .forEach(item => snackbarStore.dequeue(item.id!))
}
